// How our question and answer codes relate to standard codes such as LOINC.
import type { ConceptMap } from '@medplum/fhirtypes'
import { ASSESSMENT_TEMPLATE_QUESTION_LOINC_MAP } from '#src/terminology/assessmentTemplates.ts'
import { INSTRUMENT_QUESTION_LOINC_MAP } from '#src/terminology/instruments/instrumentTerminology.ts'
import { NUTRITION_QUESTION_LOINC_MAP } from '#src/terminology/nutrition.ts'
import {
  PCP_ANSWER_LOINC_MAP,
  PCP_QUESTION_LOINC_MAP,
} from '#src/terminology/pcpInitialAssessment.ts'
import { RD_QUESTION_LOINC_MAP } from '#src/terminology/rdInitialAssessment.ts'
import { VITAL_QUESTION_LOINC_MAP } from '#src/terminology/vitals.ts'

export const STANDARD_CODE_MAP: ConceptMap = {
  resourceType: 'ConceptMap',
  url: 'https://www.seenhealth.org/fhir/ConceptMap/assessment-to-loinc',
  version: '1',
  name: 'SeenAssessmentToLoinc',
  status: 'active',
  group: [
    {
      source: 'https://www.seenhealth.org/fhir/CodeSystem/assessment-questions',
      target: 'http://loinc.org',
      element: [
        {
          code: 'oral-med-management-1wk-brief-setup',
          target: [
            {
              code: '57285-9',
              equivalence: 'wider',
              comment:
                'OASIS M2020; ours adds a 1-week lookback, excludes inhalants, and adds one answer',
            },
          ],
        },
        {
          code: 'bladder-control-3-level',
          target: [
            {
              code: '96760-4',
              equivalence: 'equivalent',
            },
          ],
        },
        {
          code: 'daily-decisions',
          target: [
            {
              code: '45490-0',
              equivalence: 'relatedto',
              comment:
                'MDS cognitive skills for daily decision making. This keeps the five-level interRAI wording, including minimally impaired, which LL12-6 does not.',
            },
          ],
        },
        {
          code: 'bladder-control-4-level',
          target: [
            {
              code: '46553-4',
              equivalence: 'relatedto',
              comment:
                'OASIS urinary incontinence or urinary catheter presence uses three answers and does not separate occasional from daily incontinence',
            },
          ],
        },
        {
          code: 'bowel-control',
          target: [
            {
              code: '46587-2',
              equivalence: 'relatedto',
              comment:
                'OASIS bowel incontinence frequency uses a finer weekly scale plus ostomy and unknown',
            },
          ],
        },
        {
          code: 'oral-med-management-1wk-itemized-setup',
          target: [
            {
              code: '57285-9',
              equivalence: 'wider',
              comment:
                'OASIS M2020. This adds a 1-week lookback, excludes inhalants, keeps the source typo "talk", combines setup and reminders, and adds an answer for independent some of the time.',
            },
          ],
        },
        ...PCP_QUESTION_LOINC_MAP,
        ...RD_QUESTION_LOINC_MAP,
        ...ASSESSMENT_TEMPLATE_QUESTION_LOINC_MAP,
        ...INSTRUMENT_QUESTION_LOINC_MAP,
        ...NUTRITION_QUESTION_LOINC_MAP,
        ...VITAL_QUESTION_LOINC_MAP,
      ],
    },
    {
      source: 'https://www.seenhealth.org/fhir/CodeSystem/assessment-answers',
      target: 'http://loinc.org',
      element: [
        {
          code: 'oral-med-independent',
          target: [
            {
              code: 'LA6135-3',
              equivalence: 'equivalent',
            },
          ],
        },
        {
          code: 'oral-med-with-setup-or-reminders',
          target: [
            {
              code: 'LA12322-6',
              equivalence: 'wider',
            },
            {
              code: 'LA12323-4',
              equivalence: 'wider',
            },
          ],
        },
        {
          code: 'oral-med-independent-sometimes',
          target: [
            {
              equivalence: 'unmatched',
              comment: 'OASIS M2020 has no matching answer',
            },
          ],
        },
        {
          code: 'oral-med-administered-by-other',
          target: [
            {
              code: 'LA12324-2',
              equivalence: 'equivalent',
            },
          ],
        },
        {
          code: 'oral-med-none-prescribed',
          target: [
            {
              code: 'LA6290-6',
              equivalence: 'equivalent',
            },
          ],
        },
        {
          code: 'yes',
          target: [
            {
              code: 'LA33-6',
              equivalence: 'equivalent',
              comment: 'LOINC Yes',
            },
          ],
        },
        {
          code: 'no',
          target: [
            {
              code: 'LA32-8',
              equivalence: 'equivalent',
              comment: 'LOINC No',
            },
          ],
        },
        {
          code: 'decision-independent',
          target: [
            {
              code: 'LA57-5',
              equivalence: 'relatedto',
              comment: 'MDS independent; local text adds the examples',
            },
          ],
        },
        {
          code: 'decision-modified',
          target: [
            {
              code: 'LA58-3',
              equivalence: 'relatedto',
              comment:
                'MDS modified independence. Source template spells this "difficult".',
            },
          ],
        },
        {
          code: 'decision-minimal',
          target: [
            {
              equivalence: 'unmatched',
              comment:
                'MDS cognitive-skills answer list LL12-6 has no separate minimally-impaired answer',
            },
          ],
        },
        {
          code: 'decision-moderate',
          target: [
            {
              code: 'LA59-1',
              equivalence: 'relatedto',
              comment:
                'MDS moderately impaired is broader than this local definition',
            },
          ],
        },
        {
          code: 'decision-severe',
          target: [
            {
              code: 'LA60-9',
              equivalence: 'relatedto',
              comment: 'MDS severely impaired',
            },
          ],
        },
        {
          code: 'bladder-continent-no-device',
          target: [
            {
              code: 'LA6278-1',
              equivalence: 'relatedto',
              comment:
                'OASIS also treats anuria and a pouched urinary ostomy as no incontinence or catheter',
            },
          ],
        },
        {
          code: 'bladder-occasional-not-daily',
          target: [
            {
              equivalence: 'unmatched',
              comment: 'OASIS M1610 does not separate occasional incontinence',
            },
          ],
        },
        {
          code: 'bladder-incontinent-daily',
          target: [
            {
              code: 'LA6332-6',
              equivalence: 'wider',
              comment:
                'OASIS "patient is incontinent" includes any incontinence, not only multiple daily episodes',
            },
          ],
        },
        {
          code: 'bladder-device',
          target: [
            {
              code: 'LA6339-1',
              equivalence: 'relatedto',
              comment:
                'OASIS names catheter types; this answer also includes other urinary collection devices',
            },
          ],
        },
        {
          code: 'bowel-continent',
          target: [
            {
              equivalence: 'unmatched',
              comment:
                'Coarser than OASIS bowel-frequency answer LA6440-7, which includes very rare incontinence',
            },
          ],
        },
        {
          code: 'bowel-occasional',
          target: [
            {
              equivalence: 'unmatched',
              comment:
                'No single OASIS bowel-frequency answer matches this band',
            },
          ],
        },
        {
          code: 'bowel-incontinent',
          target: [
            {
              equivalence: 'unmatched',
              comment:
                'No single OASIS bowel-frequency answer matches this band',
            },
          ],
        },
        {
          code: 'oral-independent',
          target: [
            {
              code: 'LA6135-3',
              equivalence: 'relatedto',
              comment:
                'Paraphrase of the OASIS independent answer, adding "consistently" and "without any assistance or supervision"',
            },
          ],
        },
        {
          code: 'oral-setup-or-reminders',
          target: [
            {
              code: 'LA12322-6',
              equivalence: 'wider',
              comment:
                'OASIS separates advance preparation or a drug diary from reminders',
            },
            {
              code: 'LA12323-4',
              equivalence: 'wider',
              comment:
                'OASIS reminder answer is combined here with setup and a drug diary',
            },
          ],
        },
        {
          code: 'oral-administered',
          target: [
            {
              code: 'LA12324-2',
              equivalence: 'relatedto',
              comment:
                'OASIS describes inability; this answer describes what the participant does',
            },
          ],
        },
        {
          code: 'oral-none',
          target: [
            {
              code: 'LA6290-6',
              equivalence: 'relatedto',
              comment: 'OASIS answer is "No oral medications prescribed."',
            },
          ],
        },
        ...PCP_ANSWER_LOINC_MAP,
      ],
    },
    {
      source: 'https://www.seenhealth.org/fhir/CodeSystem/assessment-answers',
      target: 'http://snomed.info/sct',
      element: [
        {
          code: 'yes',
          target: [
            {
              code: '373066001',
              equivalence: 'equivalent',
              comment: 'Yes (qualifier value)',
            },
          ],
        },
        {
          code: 'no',
          target: [
            {
              code: '373067005',
              equivalence: 'equivalent',
              comment: 'No (qualifier value)',
            },
          ],
        },
      ],
    },
    {
      source: 'https://www.seenhealth.org/fhir/CodeSystem/assessment-answers',
      target: 'urn:ietf:bcp:47',
      element: [
        {
          code: 'mandarin',
          target: [
            {
              code: 'cmn',
              equivalence: 'equivalent',
              comment: 'BCP 47 language tag for Mandarin',
            },
          ],
        },
        {
          code: 'cantonese',
          target: [
            {
              code: 'yue',
              equivalence: 'equivalent',
              comment: 'BCP 47 language tag for Cantonese',
            },
          ],
        },
        {
          code: 'english',
          target: [
            {
              code: 'en',
              equivalence: 'equivalent',
              comment: 'BCP 47 language tag for English',
            },
          ],
        },
      ],
    },
  ],
}
